import { useState, type CSSProperties, type DragEvent } from "react";
import { createPortal } from "react-dom";
import type { EditorNodeData, NodePlugin } from "../../core/editor-types";

export const COMPONENT_DND_MIME = "application/x-kimnote-component";
export const KIMNOTE_COMPONENT_DRAG_MIME = COMPONENT_DND_MIME;
export const COMPONENT_DND_METADATA_MIME =
  "application/x-kimnote-component+json";

export interface ComponentDragPayload {
  type: string;
  version: number;
}

export interface ComponentToolbarProps {
  plugins: readonly NodePlugin[];
  onInsert: (componentType: string) => void;
  onComponentDragStart?: (
    event: DragEvent<HTMLButtonElement>,
    plugin: NodePlugin,
  ) => void;
  label?: string;
  disabled?: boolean;
}

interface DragPreviewState {
  plugin: NodePlugin;
  data: EditorNodeData;
  x: number;
  y: number;
}

const PREVIEW_MAX_WIDTH = 300;
const PREVIEW_MAX_HEIGHT = 220;
const PREVIEW_SCALE = 0.78;

export function ComponentToolbar({
  plugins,
  onInsert,
  onComponentDragStart,
  label = "组件",
  disabled = false,
}: ComponentToolbarProps) {
  const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null);

  const handleDragStart = (
    event: DragEvent<HTMLButtonElement>,
    plugin: NodePlugin,
  ) => {
    const payload: ComponentDragPayload = {
      type: plugin.type,
      version: plugin.version,
    };

    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(COMPONENT_DND_MIME, plugin.type);
    event.dataTransfer.setData(
      COMPONENT_DND_METADATA_MIME,
      JSON.stringify(payload),
    );
    event.dataTransfer.setData("text/plain", plugin.type);
    const transparentDragImage = document.createElement("canvas");
    transparentDragImage.width = 1;
    transparentDragImage.height = 1;
    event.dataTransfer.setDragImage(transparentDragImage, 0, 0);
    setDragPreview({ plugin, data: plugin.create(), x: event.clientX, y: event.clientY });
    onComponentDragStart?.(event, plugin);
  };

  const handleDrag = (event: DragEvent<HTMLButtonElement>) => {
    if (event.clientX === 0 && event.clientY === 0) return;
    setDragPreview((current) => current ? { ...current, x: event.clientX, y: event.clientY } : null);
  };

  return (
    <>
    <nav className="component-toolbar" aria-label="画布组件">
      <span className="component-toolbar__label">{label}</span>

      {plugins.map((plugin) => {
        const Icon = plugin.icon;
        const description = plugin.description
          ? `${plugin.title}：${plugin.description}`
          : plugin.title;

        return (
          <button
            type="button"
            key={plugin.type}
            className="toolbar-button"
            draggable={!disabled}
            onDragStart={(event) => handleDragStart(event, plugin)}
            onDrag={handleDrag}
            onDragEnd={() => setDragPreview(null)}
            onClick={() => onInsert(plugin.type)}
            disabled={disabled}
            aria-label={`添加${plugin.title}`}
            title={`${description}\n点击添加，或拖到画布`}
          >
            <Icon size={19} strokeWidth={1.8} aria-hidden="true" />
          </button>
        );
      })}
    </nav>
    {dragPreview ? <ComponentDragPreview preview={dragPreview} /> : null}
    </>
  );
}

function ComponentDragPreview({ preview }: { preview: DragPreviewState }) {
  const { plugin, data, x, y } = preview;
  const Renderer = plugin.Renderer;
  const scale = Math.min(
    PREVIEW_SCALE,
    PREVIEW_MAX_WIDTH / plugin.defaultSize.width,
    PREVIEW_MAX_HEIGHT / plugin.defaultSize.height,
  );
  const displayWidth = plugin.defaultSize.width * scale;
  const displayHeight = plugin.defaultSize.height * scale;
  const left = Math.max(8, Math.min(x + 18, window.innerWidth - displayWidth - 8));
  const top = Math.max(8, Math.min(y + 18, window.innerHeight - displayHeight - 8));
  const style = {
    left,
    top,
    width: displayWidth,
    height: displayHeight,
    "--component-preview-scale": scale,
    "--component-preview-width": `${plugin.defaultSize.width}px`,
    "--component-preview-height": `${plugin.defaultSize.height}px`,
  } as CSSProperties;

  return createPortal(
    <div
      className="component-drag-preview"
      data-component-kind={plugin.type}
      style={style}
      aria-hidden="true"
    >
      <div className="component-drag-preview__surface">
        <div className="canvas-node-renderer">
          <Renderer id={`drag-preview-${plugin.type}`} data={data} selected={false} update={() => undefined} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
